/**
 * The CSI cost decomposition panel — what the contractor's estimate actually contains, by division.
 *
 * ## The verdicts were withheld, and are now shown — except Division 01
 *
 * This panel shipped facts only. The engine classified every division against the Intelligence
 * Workbook's $/GSF bands, but those bands disagreed with each other by up to 60%, which put ten of
 * fourteen divisions OUTSIDE_RANGE on the one real GGP estimate that had run through here — a property
 * of the spec, not of the budget (#479). Rendering that would have told a GC his ordinary estimate
 * fails on most of his job on the strength of an arithmetic error in a spreadsheet.
 *
 * **#479 closed 2026-08-08**, canon corrected the bands across `intel-v2.7.4`–`v2.7.7`, and the
 * Consumer Register released them: _"the rest of the per-division panel un-withholds by ruling of
 * August 10, 2026"_. **Division 01 alone stays withheld until the canon Consumer Register's Division
 * 01 normalization row closes — that row is CANON'S, not ours.** Our half is done: both shapes were
 * demonstrated on the live path 2026-08-19 (Commons 7.68 REASONABLE unnormalized; a GC-outside budget
 * normalized by general conditions only) and our tracking issue closed. The ROW is what releases this,
 * and `pnpm chad` reports its status — so the condition stays machine-checkable rather than a note.
 *
 * ⚠️ TWO THINGS THE READER MUST BE TOLD, AND BOTH ARE RENDERED. A verdict reached on an incomplete
 * decomposition is **provisional** (canon `intel-v2.7.6`: below 95% of classified weight the bands do
 * NOT move, the verdicts qualify). And a blank verdict on Division 01 is **withheld**, not "we
 * benchmarked and found nothing" — naming a check without its report is a claim, not a gap (#492).
 */

import type {
  BudgetOmissionView,
  ConstructionDecomposition,
  ConstructionDivision,
  ScopeCoverageView,
} from '../api.js';
import { currency, integer, percentFromRatio } from '../format.js';

/** Human labels for the engine's `ProductType` union. Unknown values fall through unchanged. */
const PRODUCT_TYPE_LABELS: Record<string, string> = {
  'wood-frame-multifamily': 'Wood-frame multifamily',
  'concrete-podium': 'Concrete podium',
  'adaptive-reuse': 'Adaptive reuse',
  'industrial-flex': 'Industrial / flex',
  'commercial-retail': 'Commercial / retail',
};

/** `$126.74` — per-square-foot figures carry cents, unlike the whole-dollar totals. */
function perSf(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `$${value.toFixed(2)}`;
}

/** A total divided by GSF, for the build-up rows. Null GSF ⇒ no per-SF column rather than a wrong one. */
function ratePerSf(total: number | null | undefined, gsf: number | null | undefined): string {
  if (total === null || total === undefined || !Number.isFinite(total)) return '—';
  if (!gsf || !Number.isFinite(gsf) || gsf <= 0) return '—';
  return perSf(total / gsf);
}

/**
 * A division's benchmark verdict — or, on Division 01, the fact that it is WITHHELD.
 *
 * ⚠️ An em dash would read as "we benchmarked and found nothing". The projection drops the verdict
 * keys for Division 01 alone, so an absent status here is always the deliberate withholding, and it
 * is labelled as such with the footnote below carrying the reason.
 */
function Verdict({ division }: { division: ConstructionDivision }) {
  if (!division.status) return <span className="csi__withheld">Withheld</span>;
  const band =
    typeof division.adjLow === 'number' && typeof division.adjHigh === 'number'
      ? `${perSf(division.adjLow)}–${perSf(division.adjHigh)}`
      : null;

  /*
    ⚠️ "In band" BESIDE A FIGURE OUTSIDE THE PRINTED BAND READS AS A BUG.

    Measured on a real staging run (`01a00fcf-1230`, CityView): Division 01 came back
    `$10.00 · In band vs $6.38–$9.35`. Both halves are true — `REASONABLE` is a TOLERANCED verdict, so
    a value up to 10% above the ceiling still clears — but the row prints the CALIBRATED band and the
    TOLERANCED verdict side by side and never says they are different rulers. A reader checking the
    arithmetic concludes the verdict is broken, and a reader who does not check learns nothing.

    ⚠️ The tolerance constants stay in the ENGINE. Nothing here re-derives 1.1 or 0.75: if the engine
    says `REASONABLE` and the figure sits outside the printed band, it is inside the tolerance BY
    DEFINITION. Copying the multipliers into the web would make a second source of truth for a
    canon-ruled threshold, which is exactly the drift `canon:check` exists to stop.
  */
  /*
    The engine now SERVES the position (`bandPosition`), so this surface no longer has to work it out
    — but `analyses.result` is a stored snapshot and runs persisted before 2026-08-26 have no such
    key. The local comparison stays as the fallback for those, and only for those: dropping it would
    silently regress every existing analysis to a bare "In band" beside a figure outside the band,
    which is the exact defect #722 fixed.
  */
  const derivedPosition =
    typeof division.perGSF === 'number' &&
    typeof division.adjLow === 'number' &&
    typeof division.adjHigh === 'number'
      ? division.perGSF > division.adjHigh
        ? 'above'
        : division.perGSF < division.adjLow
          ? 'below'
          : 'within'
      : 'within';
  const position = division.bandPosition ?? derivedPosition;

  /*
    ⚠️ "Within tolerance" ALONE DOES NOT SAY WHICH SIDE. It read identically for a figure above the
    ceiling and one below the floor — opposite facts about a budget, printed the same way.

    Canon ruling (Chad Garrett, 2026-08-25): *"the report states exactly that — above the band,
    within tolerance — and the printed band stays as calibrated."*
  */
  const label =
    division.status === 'REASONABLE' && position !== 'within'
      ? position === 'above'
        ? 'Above band, within tolerance'
        : 'Below band, within tolerance'
      : (VERDICT_LABELS[division.status] ?? division.status);

  return (
    <>
      <span className={`csi__verdict csi__verdict--${division.status.toLowerCase()}`}>{label}</span>
      {band && <span className="csi__band"> vs {band}</span>}
    </>
  );
}

/** The engine's `DivisionStatus`, in the reader's words. Unknown values fall through unchanged. */
const VERDICT_LABELS: Record<string, string> = {
  REASONABLE: 'In band',
  AGGRESSIVE: 'Aggressive',
  OUTSIDE_RANGE: 'Outside band',
  UNBANDED: 'No band',
  UNKNOWN_DIVISION: 'Unrecognized',
};

/** Why the verdicts are provisional, in the reader's words rather than the enum's. */
const PROVISIONAL_REASONS: Record<string, string> = {
  'partial-weight-coverage':
    'The estimate classifies only part of the scope canon expects for this product type, so each division is measured against a band sized for the whole job.',
  'partial-dollar-coverage':
    'Part of the accounted construction cost is not attributed to any division, so the division shares understate the job.',
  'non-canon-gsf-basis':
    'The area basis is a proxy rather than the one the bands were calibrated on, so the $/GSF figures are not strictly comparable.',
};

/**
 * SCOPE COVERAGE (ADR-0069 A11): the divisions canon expects for this product type that the estimate
 * carries no line for. `csiScopeCoverage` was built and nothing called it; this is where it is read.
 *
 * ⚠️ NO SEVERITY WORDS. The engine labels each gap minor / material / critical, but those cutoffs are
 * OUR starting values (`scope-coverage.ts`), not canon's, and nothing has measured them. What is canon is
 * each division's expected share of the budget (Intelligence Workbook sheet 3), so that is what is shown,
 * heaviest first as the engine serves them.
 *
 * ⚠️ "No division line" is NOT "not priced". A gap may be priced inside a line the estimate never broke
 * out by division (a "Sitework" lump can hold 31, 32 and 33), or inside a hard-cost total that exceeds
 * the itemized divisions by more than contractor loading explains (`reconcile.status === 'large-gap'`,
 * the engine's own threshold). Either way the reader is told, and a named lump is named.
 */
function ScopeCoverageSection({
  coverage,
  construction,
  omissions,
}: {
  coverage: ScopeCoverageView;
  construction: ConstructionDecomposition;
  omissions: readonly BudgetOmissionView[];
}) {
  const gaps = coverage.gaps ?? [];
  const capped = coverage.cappedCodes ?? [];
  const unattributed = coverage.unattributedDollars ?? 0;
  const rec = construction.reconcile ?? null;
  const hardCostGap =
    typeof rec?.hardCosts === 'number' && typeof construction.divisionSum === 'number'
      ? rec.hardCosts - construction.divisionSum
      : null;
  const largeGap = rec?.status === 'large-gap';
  // Only an UN-ITEMIZED lump can hold a modelled division. Equipment and furnishings (11, 12) sit outside
  // the divisions canon weights, so naming them here would suggest they could close a gap they cannot.
  const lumps = omissions.filter((o) => o.reason === 'un-itemized' && o.dollars > 0);
  return (
    <div className="csi__coverage" data-testid="scope-coverage">
      <h4 className="csi__coverage-title">Scope coverage</h4>
      {gaps.length === 0 ? (
        <p className="card__prose">
          The estimate carries a line in every division canon expects for this product type.
        </p>
      ) : (
        <>
          <p className="card__prose">
            {gaps.length} division{gaps.length === 1 ? '' : 's'} canon expects for this product type{' '}
            {gaps.length === 1 ? 'has' : 'have'} no line in the estimate
            {typeof coverage.missingWeight === 'number'
              ? `, about ${percentFromRatio(coverage.missingWeight, 0)} of the budget canon expects`
              : ''}
            .
          </p>
          <ul className="csi__gaps">
            {gaps.map((g) => (
              <li key={g.code}>
                <span className="csi__code">{g.code}</span> {g.name}
                {' · '}
                canon expects {percentFromRatio(g.expectedWeight, 1)} of the budget here
              </li>
            ))}
          </ul>
        </>
      )}
      {capped.length > 0 && (
        <p className="card__note">
          Division{capped.length === 1 ? '' : 's'} {capped.join(', ')}{' '}
          {capped.length === 1 ? 'is' : 'are'} priced as one un-itemized line, so{' '}
          {capped.length === 1 ? 'it counts' : 'they count'} as covered but{' '}
          {capped.length === 1 ? 'has' : 'have'} no row in the table above.
        </p>
      )}
      {gaps.length > 0 && (lumps.length > 0 || unattributed > 0 || largeGap) && (
        <p className="card__note" data-testid="scope-coverage-caveat">
          {lumps.length > 0
            ? `${lumps.map((o) => `${currency(o.dollars)} listed as “${o.label}”`).join(' and ')} may cover some of these gaps. `
            : unattributed > 0
              ? `${currency(unattributed)} of base cost is not attributed to any division, and some of these gaps may be priced inside it. `
              : ''}
          {largeGap && hardCostGap !== null && hardCostGap > 0
            ? `The guaranteed maximum price exceeds the itemized divisions by ${currency(hardCostGap)}, more than contractor loading usually explains, so some of these gaps may be priced there. `
            : ''}
          Read each one as &ldquo;no division line&rdquo;, not as &ldquo;not priced&rdquo;.
        </p>
      )}
    </div>
  );
}

export function ConstructionCard({
  construction,
  scopeCoverage = null,
  omissions = null,
}: {
  construction: ConstructionDecomposition;
  /** ADR-0069 A11. Absent on free-tier responses and on runs that predate the field. */
  scopeCoverage?: ScopeCoverageView | null;
  /** The budget read's omitted lines, when the run recorded them (ingestion runs). Names a lump. */
  omissions?: readonly BudgetOmissionView[] | null;
}) {
  const divisions = construction.divisions ?? [];
  const bench = construction.benchmarking ?? null;
  const gsf = construction.totalGSF ?? null;
  const rec = construction.reconcile ?? null;
  const omitted = rec?.omittedDollars ?? 0;
  const accounted = rec?.accountedTotal ?? null;
  const productLabel = construction.productType
    ? (PRODUCT_TYPE_LABELS[construction.productType] ?? construction.productType)
    : null;

  // The omitted share is measured against what we can account for, not against the GMP: it answers
  // "how much of the base cost did the decomposition NOT classify", and the GMP includes loading that
  // was never a candidate for classification. On the first real estimate this was 8.3%.
  const omittedShare = omitted > 0 && accounted && accounted > 0 ? omitted / accounted : null;

  return (
    <section className="card">
      <h3 className="card__title">
        Construction budget
        {divisions.length > 0 && (
          <span className="pill pill--badge">
            {divisions.length} division{divisions.length === 1 ? '' : 's'}
          </span>
        )}
      </h3>

      <p className="card__prose">
        Decomposed from the contractor&rsquo;s estimate
        {productLabel ? ` — ${productLabel}` : ''}
        {gsf ? `, ${integer(gsf)} GSF` : ''}.
      </p>

      {rec && (
        <div className="csi__buildup">
          <BuildupRow label="Classified divisions" amount={construction.divisionSum} gsf={gsf} />
          {omitted > 0 && (
            <BuildupRow
              label="Not attributed to a division"
              amount={omitted}
              gsf={gsf}
              note={
                omittedShare !== null
                  ? `${percentFromRatio(omittedShare, 1)} of base cost`
                  : undefined
              }
            />
          )}
          <BuildupRow label="Base construction cost" amount={accounted} gsf={gsf} strong />
          {rec.hardCosts !== null && rec.hardCosts !== undefined && (
            <BuildupRow label="Guaranteed maximum price" amount={rec.hardCosts} gsf={gsf} strong />
          )}
          {rec.gap !== null && rec.gap !== undefined && (
            <div className="csi__row csi__row--gap">
              <span className="csi__label">Contractor loading</span>
              <span className="csi__amount">{percentFromRatio(rec.gap, 1)}</span>
              <span className="csi__rate">of GMP</span>
            </div>
          )}
        </div>
      )}

      {omitted > 0 && (
        <p className="card__note">
          Lines the estimate states without a division breakdown are left out of the table rather
          than assigned to one bucket, so they are added back above. They are base construction
          cost, not contractor overhead — counting them as loading would overstate it by exactly
          this amount.
        </p>
      )}

      {divisions.length > 0 && (
        <div className="csi__tablewrap">
          <table className="csi__table">
            <thead>
              <tr>
                <th scope="col">Division</th>
                <th scope="col" className="csi__num">
                  Budget
                </th>
                <th scope="col" className="csi__num">
                  $/GSF
                </th>
                <th scope="col" className="csi__num">
                  Share
                </th>
                <th scope="col">Vs. benchmark</th>
              </tr>
            </thead>
            <tbody>
              {divisions.map((d, i) => (
                <tr key={`${d.code ?? i}`}>
                  <th scope="row">
                    <span className="csi__code">{d.code}</span> {d.name}
                  </th>
                  <td className="csi__num">{currency(d.dollars)}</td>
                  <td className="csi__num">{perSf(d.perGSF)}</td>
                  <td className="csi__num">{percentFromRatio(d.share, 1)}</td>
                  <td>
                    <Verdict division={d} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scopeCoverage && (
        <ScopeCoverageSection
          coverage={scopeCoverage}
          construction={construction}
          omissions={omissions ?? []}
        />
      )}

      {construction.lumpedDivision && (
        <p className="card__note csi__note--warn">
          <strong>{construction.lumpedDivision}</strong> holds more than half the classified budget
          on its own, which usually means the estimate lumps several trades into one line rather
          than that the trade is genuinely that large.
        </p>
      )}

      {bench?.provisional && (
        /*
          ⚠️ Canon rules the verdicts PROVISIONAL below 95% of classified weight — it does NOT move
          the bands, because renormalising grades a budget on a curve set by whatever the sponsor
          happened to classify (`intel-v2.7.6`). So the verdicts show, and they show qualified.
        */
        <p className="card__note csi__note--warn">
          <strong>These verdicts are provisional.</strong>{' '}
          {PROVISIONAL_REASONS[bench.provisionalReasons?.[0] ?? ''] ??
            'The decomposition does not cover enough of the project to benchmark against.'}{' '}
          {typeof bench.classifiedWeight === 'number' &&
            `The classified divisions carry ${percentFromRatio(bench.classifiedWeight, 0)} of the
             expected scope for this product type.`}{' '}
          A band comparison on an incomplete denominator reports our own coverage as a finding about
          this budget, so read these as indicative rather than as findings.
        </p>
      )}

      {/*
        WHAT THE BANDS WERE ADJUSTED BY. Every "vs. benchmark" verdict above is measured against bands
        scaled by a regional cost multiplier, and until now the reader was shown the verdicts and never
        the scaling. The field's own contract calls it provenance — "feeds the benchmark bands ONLY, so
        it is reported as provenance, not a figure" — and it was reported to nobody: served on the
        payload, rendered by nothing, found by the orphan-field sweep (#683).

        That matters here more than most places. A division can fail its band because the project is
        expensive or because the metro multiplier is 1.18, and a reader who cannot see which cannot
        check the call — the same argument the provisional note above makes.

        `gap: true` means no multiplier was resolved for this metro, which is NOT the same as 1.0 and is
        said in words rather than shown as a neutral-looking number.
      */}
      {construction.regional && (
        <p className="card__note">
          {construction.regional.gap ? (
            <>
              <strong>No regional adjustment was applied.</strong> We could not resolve a cost index
              for this market, so the bands above are the national ones &mdash; read them as a
              coarser comparison, not a local one.
            </>
          ) : (
            typeof construction.regional.multiplier === 'number' && (
              <>
                <strong>
                  Benchmark bands are regionally adjusted (&times;
                  {construction.regional.multiplier.toFixed(2)}).
                </strong>{' '}
                {construction.regional.basis ? `Basis: ${construction.regional.basis}. ` : ''}
                The division figures above are as estimated; only the bands they are compared
                against move with this index.
              </>
            )
          )}
        </p>
      )}

      {bench?.division01Withheld && (
        // Naming a check without its report is a claim, not a gap (#492). A blank verdict beside a
        // $/GSF figure must say WHICH blank it is.
        <p className="card__note">
          <strong>Division 01 carries no verdict.</strong> Its benchmark is withheld pending a canon
          ruling on how general conditions are normalized before comparison &mdash; the
          division&rsquo;s own figures above are read from the estimate and are unaffected.
        </p>
      )}
    </section>
  );
}

/** One line of the division-sum → GMP build-up. */
function BuildupRow({
  label,
  amount,
  gsf,
  note,
  strong,
}: {
  label: string;
  amount: number | null | undefined;
  gsf: number | null;
  note?: string;
  strong?: boolean;
}) {
  return (
    <div className={`csi__row${strong ? ' csi__row--strong' : ''}`}>
      <span className="csi__label">
        {label}
        {/* Parenthesised, not merely styled: at 12.5px grey the qualifier still sits on the same line,
            and "Not attributed to a division 8.3% of base cost" reads as one run-on phrase. */}
        {note && <span className="csi__sub">&nbsp;({note})</span>}
      </span>
      <span className="csi__amount">{currency(amount)}</span>
      <span className="csi__rate">{ratePerSf(amount, gsf)}</span>
    </div>
  );
}
